import { useCallback, useEffect, useRef, useState } from "react";
import { getAssignedTasks } from "../services/dealTasksApi";
import type { DealTask, TaskStatus } from "../types/dealTask";

interface UseMyTasksResult {
  tasks: DealTask[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

function sortTasksNewestFirst(tasks: DealTask[]): DealTask[] {
  return [...tasks].sort((a, b) => {
    const dateA = new Date(a.created_at ?? 0).getTime();
    const dateB = new Date(b.created_at ?? 0).getTime();
    return dateB - dateA;
  });
}

export function useMyTasks(
  userId?: number,
  status?: TaskStatus,
): UseMyTasksResult {
  const [tasks, setTasks] = useState<DealTask[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Only the latest request for the current userId/status may update state; older
  // responses (e.g. for a previous user, or from a stale refetch closure) are ignored.
  const latestRequestIdRef = useRef(0);
  const paramsRef = useRef({ userId, status });

  const refetch = useCallback(async () => {
    if (!userId) return;
    // A refetch captured before userId/status changed must not invalidate the current request.
    if (paramsRef.current.userId !== userId || paramsRef.current.status !== status) return;
    const requestId = ++latestRequestIdRef.current;
    const isStale = () =>
      requestId !== latestRequestIdRef.current ||
      paramsRef.current.userId !== userId ||
      paramsRef.current.status !== status;
    setLoading(true);
    setError(null);
    try {
      const response = await getAssignedTasks(userId, status);
      if (isStale()) return;
      if (response?.success && response.data) {
        const visible = response.data.filter((t) => !t.is_deleted);
        setTasks(sortTasksNewestFirst(visible));
      } else {
        setError(
          typeof response?.message === "string"
            ? response.message
            : "Failed to load tasks",
        );
      }
    } catch (err) {
      if (isStale()) return;
      setError(err instanceof Error ? err.message : "Failed to load tasks");
    } finally {
      if (!isStale()) setLoading(false);
    }
  }, [userId, status]);

  useEffect(() => {
    paramsRef.current = { userId, status };
    void refetch();
    return () => {
      // Invalidate any in-flight request for the previous userId/status (or on unmount).
      latestRequestIdRef.current += 1;
      setLoading(false);
    };
  }, [userId, status, refetch]);

  return { tasks, loading, error, refetch };
}
